<template>
  <label
    class="counter"
    :class="{
      'counter--active': hasHover || hasFocus,
      'counter--disabled': disableInputEdit,
    }"
    @mouseover="mouseOverHandler"
    @mouseleave="mouseOutHandler"
    ref="wrapper"
    :aria-label="$t('checkout.basket.position.header.quantity') + ' ' + value"
  >
    <template
      v-if="disableInputEdit"
    >
      <span class="counter__input counter__input--disabled">{{ value }}</span>
    </template>
    <template
      v-else
    >
      <input
        :type="type"
        inputmode="numeric"
        class="counter__input"
        :name="name"
        v-model="value"
        @keyup="keyUpHandler"
        @focus="focusHandler"
        @blur="blurHandler"
        :disabled="disableInputEdit"
        :min="minQuantity"
        :max="maxQuantity"
        ref="input"
        autocomplete="off"
        autocorrect="off"
      >
      <button
        aria-label="+1"
        class="counter__button counter__button--increase"
        :class="{
          'counter__button--disabled': this.value === this.maxQuantity
        }"
        tabindex="-1"
        type="button"
        @click.prevent="increase"
      />
      <button
        aria-label="-1"
        class="counter__button counter__button--decrease"
        :class="{
          'counter__button--disabled': this.value === this.minQuantity
        }"
        tabindex="-1"
        type="button"
        @click.prevent="decrease"
      />
    </template>
  </label>
</template>

<script lang="ts">
  import {Vue, Options, Prop, Watch} from 'vue-property-decorator';
  import type Counter from '../../types/Counter';

  @Options({
    name: 'counter-input',
    emits: ['update']
  })
  export default class CounterInput extends Vue {
    @Prop({required: true})
    counter!: Counter;
    @Prop({default: false})
    onlyOneItem!: boolean;
    @Prop({default: 'quantity'})
    name: string;
    @Prop({type: Boolean, default: false})
    disableInputEdit: boolean;

    minQuantity: number = this.counter.minQuantity;
    maxQuantity: number = this.counter.maxQuantity;
    tradeQuantity: number = this.counter.tradeQuantity || 1;
    value: number|string = !this.onlyOneItem ? this.counter.preselectedQuantity : 1;
    hasFocus: boolean = false;
    hasHover: boolean = false;
    isTouch: boolean = null;
    type: 'number'|'text' = null;

    focusValue: number|string;

    get numericValue() {
      return Number(this.value);
    }

    @Watch('counter.preselectedQuantity')
    onCounterChange() {
      this.value = this.counter.preselectedQuantity;
    }

    created() {
      this.isTouch = 'ontouchstart' in window;
      this.type = this.isTouch ? 'number' : 'text';
      if (this.tradeQuantity > 1 && this.maxQuantity >= this.tradeQuantity) {
        this.minQuantity = Math.ceil(this.minQuantity / this.tradeQuantity) * this.tradeQuantity;
        this.maxQuantity = Math.floor(this.maxQuantity / this.tradeQuantity) * this.tradeQuantity;
        if (this.numericValue < this.tradeQuantity) {
          this.value = this.tradeQuantity;
        }
      }
    }

    increase() {
      let newQuantity = this.numericValue + this.tradeQuantity;

      if (newQuantity <= this.maxQuantity) {
        this.value = newQuantity;
        this.$emit('update', this.numericValue);
      }
    }

    decrease() {
      let newQuantity = this.numericValue - this.tradeQuantity;

      if (newQuantity >= this.minQuantity) {
        this.value = newQuantity;
        this.$emit('update', this.numericValue);
      }
    }

    keyUpHandler(event) {
      if (event.key === 'Enter' || event.key === 13) {
        event.preventDefault();
        this.hasHover = false;
        this.hasFocus = false;
        (<HTMLElement> this.$refs.input).blur();
      }
      if (event.key === 'ArrowUp' || event.key === 38) {
        this.increase();
      }
      if (event.key === 'ArrowDown' || event.key === 40) {
        this.decrease();
      }
    }

    blurHandler() {
      const isWrapperChildActive = (<HTMLElement> this.$refs.wrapper).querySelector(':focus, :hover');
      this.hasFocus = false;

      if (!isWrapperChildActive) {
        this.hasHover = false;
        if (this.value === '') {
          this.value = this.focusValue && Number(this.focusValue) >= this.minQuantity ? this.focusValue : this.minQuantity;
        } else {
          if (typeof this.value === 'string') {
            this.value = this.value.replace(/[^0-9]/g, '');
          }
        }
      }

      this.value = Number(this.value);
      if (this.tradeQuantity > 1 && this.maxQuantity >= this.tradeQuantity) {
        this.value = Math.ceil(this.value / this.tradeQuantity) * this.tradeQuantity;
      }
      this.value = Math.max(this.value, this.minQuantity);
      this.value = Math.min(this.value, this.maxQuantity);
      this.$emit('update', this.numericValue);
    }

    focusHandler() {
      this.hasFocus = true;
      this.focusValue = this.value;
      this.value = '';
    }

    mouseOverHandler() {
      this.hasHover = true;
    }

    mouseOutHandler() {
      this.hasHover = false;
    }

    setValue(value: number) {
      this.value = value;
    }
  }

</script>
